"use client";
// РУКА 2. Фото товара в карточке: крупное фото, миниатюры, по нажатию — на весь экран («Увеличить фото», макет).
// На весь экран: листать стрелками, клавишами ← → или пальцем; нажатие на фото — увеличить вдвое
// (увеличенное двигается за мышью или пальцем); закрыть — крестик, Esc или нажатие на тёмный фон.
// Фото нет — значок раздела, как раньше (увеличивать нечего).
import { useEffect, useState, type ReactNode } from "react";
import { PhotoSlot } from "./PhotoSlot";

export function Gallery({
  photos,
  alt,
  categoryId,
  overlay,
}: {
  photos: string[];
  alt: string;
  categoryId: string | null;
  overlay?: ReactNode; // метки поверх фото
}) {
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);

  if (photos.length === 0) {
    return (
      <div className="relative aspect-square rounded-card overflow-hidden border border-line">
        <PhotoSlot alt={alt} categoryId={categoryId} large />
        {overlay}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Увеличить фото"
        className="relative block w-full aspect-square rounded-card overflow-hidden border border-line bg-white cursor-zoom-in group"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={photos[i]} alt={alt} className="w-full h-full object-contain" />
        {overlay}
        <span className="absolute right-2 bottom-2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center text-ink2 group-hover:text-aq-d">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2" />
            <path d="M15.5 15.5L20 20M10.5 7.5v6M7.5 10.5h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      </button>

      {photos.length > 1 ? (
        <div className="mt-2 flex gap-2 overflow-x-auto [scrollbar-width:none]">
          {photos.map((src, k) => (
            <button
              key={src}
              type="button"
              onClick={() => setI(k)}
              aria-label={`Фото ${k + 1}`}
              aria-current={k === i}
              className={`w-14 h-14 flex-none rounded-lg overflow-hidden border-2 bg-white ${k === i ? "border-aq" : "border-line"}`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}

      {open ? <Viewer photos={photos} alt={alt} start={i} onClose={(k) => { setI(k); setOpen(false); }} /> : null}
    </div>
  );
}

const clamp = (v: number) => Math.min(100, Math.max(0, v));

// Где на фото нажали, в % (для увеличения в этой точке). Фото может быть уже увеличено — считаем от исходного.
function pointIn(img: HTMLElement, cx: number, cy: number) {
  const r = img.getBoundingClientRect();
  return { x: clamp(((cx - r.left) / r.width) * 100), y: clamp(((cy - r.top) / r.height) * 100) };
}

function Viewer({ photos, alt, start, onClose }: { photos: string[]; alt: string; start: number; onClose: (i: number) => void }) {
  const [i, setI] = useState(start);
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 }); // точка увеличения, % от фото
  const [drag, setDrag] = useState<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const [touchX, setTouchX] = useState<number | null>(null);
  const n = photos.length;
  const go = (d: number) => {
    setZoom(false);
    setI((k) => (k + d + n) % n);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose(i);
      if (e.key === "ArrowRight" && n > 1) go(1);
      if (e.key === "ArrowLeft" && n > 1) go(-1);
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden"; // страница под фото не прокручивается
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  });

  const arrow = "absolute top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 text-d0 flex items-center justify-center shadow hover:bg-white";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={() => onClose(i)}
      onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchX === null || zoom || n < 2) return;
        const dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        setTouchX(null);
      }}
    >
      <div className="overflow-hidden max-w-[94vw] max-h-[86vh]" onClick={(e) => e.stopPropagation()}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={photos[i]}
          alt={alt}
          onClick={(e) => {
            if (!zoom) setOrigin(pointIn(e.currentTarget, e.clientX, e.clientY));
            setZoom((z) => !z);
          }}
          onMouseMove={(e) => {
            if (zoom && !drag) setOrigin(pointIn(e.currentTarget, e.clientX, e.clientY));
          }}
          // Палец двигает увеличенное фото: тянешь вправо — фото едет вправо (точка увеличения — влево).
          onTouchStart={(e) => {
            if (zoom && e.touches.length === 1) setDrag({ x: e.touches[0].clientX, y: e.touches[0].clientY, ox: origin.x, oy: origin.y });
          }}
          onTouchMove={(e) => {
            if (!zoom || !drag || e.touches.length !== 1) return;
            const r = e.currentTarget.getBoundingClientRect(); // размер уже увеличенного фото
            const w = r.width / 2;
            const h = r.height / 2;
            setOrigin({
              x: clamp(drag.ox - ((e.touches[0].clientX - drag.x) / w) * 100),
              y: clamp(drag.oy - ((e.touches[0].clientY - drag.y) / h) * 100),
            });
          }}
          onTouchEnd={() => setDrag(null)}
          style={{
            transform: zoom ? "scale(2)" : "none",
            transformOrigin: `${origin.x}% ${origin.y}%`,
            touchAction: zoom ? "none" : "auto", // пока увеличено, палец двигает фото, а не страницу
            transition: drag ? "none" : undefined,
          }}
          className={`block max-w-[94vw] max-h-[86vh] object-contain transition-transform duration-200 ${zoom ? "cursor-zoom-out" : "cursor-zoom-in"}`}
        />
      </div>

      <button
        type="button"
        aria-label="Закрыть"
        onClick={() => onClose(i)}
        className="absolute top-3 right-3 w-11 h-11 rounded-full bg-white/90 text-d0 flex items-center justify-center shadow hover:bg-white"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M3 3l12 12M15 3L3 15" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      </button>

      {n > 1 ? (
        <>
          <button type="button" aria-label="Предыдущее фото" className={`${arrow} left-3`} onClick={(e) => { e.stopPropagation(); go(-1); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button type="button" aria-label="Следующее фото" className={`${arrow} right-3`} onClick={(e) => { e.stopPropagation(); go(1); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/90 text-sm tabular-nums">
            {i + 1} / {n}
          </div>
        </>
      ) : null}
    </div>
  );
}
